import { LitElement, html, css, unsafeCSS, property } from "lit-element";

import { seriesApi } from "../../api/seriesApi";

import { DropdownModel } from "../../models/components/DropdownModel";
import { SeriesModel } from "../../models/media/SeriesModel";

import Dropdown from "../dropdown/dropdown";

import passageReportStyles from "./passage-report.scss";
import { TeamModel } from "../../models/media/TeamModel";
import { teamsApi } from "../../api/teamsApi";
import { playersApi } from "../../api/playersApi";
import { PlayerModel, PlayerSeasonHistoryModel } from "../../models/media/PlayerModel";
import ScoreTypes from "../../enums/score-types";
import { seasonsApi } from "../../api/seasonsApi";
import { SeasonModel } from "../../models/media/SeasonModel";
import { performanceMediaApi } from "../../api/performanceMediaApi";
import { PassageBreakdownType, PassageReportModel, RangeTypes } from "../../models/media/PassageReportModel";

export default class PassageReport extends LitElement {
    @property({ type: Object })
    passageReport: PassageReportModel;

    private _matchId = "";
    private _performanceMediaUrl = "";
    private _userId = "";

    private _performanceMediaApi: performanceMediaApi;

    constructor(
        matchId: string,
        performanceMediaUrl: string,
        userId: string) {
        super();

        this._performanceMediaUrl = performanceMediaUrl;
        this._userId = userId;
        this._matchId = matchId;

        this._performanceMediaApi = new performanceMediaApi(this._performanceMediaUrl, this._userId);

        this.passageReport = null;
    }

    async firstUpdated() {

        this.showLoading(true, document.querySelector('#loading-container'));

        this.passageReport = await this._performanceMediaApi.GetPassageReport(this._matchId);

        this.showLoading(false, document.querySelector('#loading-container'));
    }

    showLoading(isLoading: boolean, container: HTMLElement) {
        if (isLoading) {
            container.innerHTML = "Loading...";
        }
        else {
            container.innerHTML = "";
        }
    }

    createRenderRoot() {
        return this;
    }

    renderPassageReport() {
        return html`<div>
            <div class="row">  
               ${this.renderPassageMetaData()}
            </div>
            <div class="row">  
                <div class="col col-md-5">
                    <div class="row">  
                        ${this.renderPassageTimes()}
                    </div>
                    <div class="row">  
                        ${this.renderPassageBreakdowns()}
                    </div>
                    <div class="row">  
                        ${this.renderTeamPossessions()}
                    </div>
                    <div class="row">  
                        ${this.renderBOPBreakdown()}
                    </div>
                </div>
                <div class="col col-md-7">
                    ${this.renderPassagesTable()}
                </div>
            </div>
        </div>`;
    }

    renderPassageMetaData() {
        return html`<table class="table table-sm table-borderless">
        <tr>
            <td>Competition:</td>
            <td>${this.passageReport.seasonSeriesName}</td>
        <tr>
        <tr>
            <td>Match:</td>
            <td>${this.passageReport.matchName}</td>
        <tr>
        <tr>
            <td>Round:</td>
            <td>${this.passageReport.round}</td>
        <tr>
        <tr>
            <td>Date:</td>
            <td>${this.passageReport.dateString}</td>
        <tr>
        <tr>
            <td>Referee:</td>
            <td>${this.passageReport.refereeName}</td>
        <tr>
    </table>`;
    }

    renderPassagesTable() {
        return html`
        <div class="passages-card">
        <table class="table table-borderless">
            <thead>
                    <tr> 
                    <th>PASSAGE</th>
                    <th>PASSAGE DURATION</th>
                    <th>PASSAGE SECONDS</th>
                    <th>TEAM</th>
                    <th>POSSESSION</th>
                    <th>STATUS</th>
                    <th>HALF</th>
                    <th>LOCATION</th>
                    <th>PHASES</th>
                    <th>START</th>
                    <th>END</th>
                    <th>DURATION</th>
                    <th>SCORED</th>
                </tr>
            </thead>
            <tbody>
                ${this.passageReport.passages.map((passage) => html`
                    ${passage.passageItems.map((passageItem, index) => html`
                    <tr>
                        <td class="center-col">${index === Math.floor(passage.passageItems.length / 2) && passage.index ? passage.index : ''}</td>
                        <td class="center-col">${index === Math.floor(passage.passageItems.length / 2) && passage.index ? passage.durationString : ''}</td>
                        <td class="center-col">${index === Math.floor(passage.passageItems.length / 2) && passage.index ? passage.durationSeconds : ''}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.teamName}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.startEvent.eventCodeName}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.rangeTypeName}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.segmentName}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.startEvent.fieldLocation}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.phaseCount === 0 ? '' : passageItem.phaseCount}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.startEvent.timeString}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.endEvent.timeString}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.durationString}</td>
                        <td style="background-color: ${passageItem.color}; color: ${this.getColorByBgColor(passageItem.color)}">${passageItem.endsInScoringEvent ? html`${passageItem.scoringEventName}` : ''}</td>
                    <tr>`)}
                    `)}
            </tbody>
        </table>
        </div>`
    };

    renderPassageTimes() {
        return html`
        <div class="passages-card">
        <table class="table table-borderless table-sm center-table-cells">
        <thead>
            <tr>
                <th>Match Time</th>
                <th>BIP Time</th>
                <th>ABIP Time</th>
                <th>BOP Time</th>
            <tr>
            </thead>
        <tbody>
            <tr>
                <td>${this.passageReport.passageTimes.find(x => x.rangeType === RangeTypes.MatchTime)?.durationString}</td>
                <td>${this.passageReport.passageTimes.find(x => x.rangeType === RangeTypes.BIPTime)?.durationString}</td>
                <td>${this.passageReport.passageTimes.find(x => x.rangeType === RangeTypes.ABIPTime)?.durationString}</td>
                <td>${this.passageReport.passageTimes.find(x => x.rangeType === RangeTypes.BOPTime)?.durationString}</td>
            </tr>
        </tbody>
    </table>
    </div>`;
    };

    renderPassageBreakdowns() {
        return html`
        <div class="passages-card">
        <table class="table table-borderless table-sm center-table-cells">
        <thead>
            <tr>
                <th>TOTAL PASSAGES</th>
                <th>Count</th>
                <th>Average Duration</th>
            <tr>
            </thead>
        <tbody>
            <tr>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.Total)?.value}</td>
                <td></td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.Average)?.valueString}</td>
            </tr>
            <tr>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.LessThan30s)?.description}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.LessThan30s)?.count}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.LessThan30s)?.valueString}</td>
            </tr>
            <tr>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan30s)?.description}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan30s)?.count}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan30s)?.valueString}</td>
            </tr>
            <tr>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan45s)?.description}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan45s)?.count}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan45s)?.valueString}</td>
            </tr>
            <tr>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan60)?.description}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan60)?.count}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan60)?.valueString}</td>
            </tr>
            <tr>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan90s)?.description}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan90s)?.count}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan90s)?.valueString}</td>
            </tr>
            <tr>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan120s)?.description}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan120s)?.count}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan120s)?.valueString}</td>
            </tr>
            <tr>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan180s)?.description}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan180s)?.count}</td>
                <td>${this.passageReport.passageBreakdowns.find(x => x.type === PassageBreakdownType.GreaterThan180s)?.valueString}</td>
            </tr>
        </tbody>
    </table>
    </div>`;
    }

    renderTeamPossessions() {
        return html`
        <div class="passages-card">
        <table class="table table-borderless table-sm center-table-cells">
            <thead>
                <tr>
                    <th>POSSESSIONS</th>
                    <th>${this.passageReport.homeTeam.name} Possessions</th>
                    <th>${this.passageReport.awayTeam.name} Possessions</th>
                </tr>
                <tr>
                    <th>Count:</th>
                    <th>${this.passageReport.homeTeam.possessionBreakdown.totalCount}</th>
                    <th>${this.passageReport.awayTeam.possessionBreakdown.totalCount}</th>
                </tr>
                <tr>
                    <th>Duration:</th>
                    <th>${this.passageReport.homeTeam.possessionBreakdown.totalDurationString}</th>
                    <th>${this.passageReport.awayTeam.possessionBreakdown.totalDurationString}</th>
                </tr>
            </thead>
            <tbody>
                ${this.passageReport.teamRanges.map((range) =>
            html`
                    <tr>
                        <td>${range.eventCodeName}</td>
                        <td>${range.homeCount}</td>
                        <td>${range.awayCount}</td>
                    </tr>`
        )}
            </tbody>
    </table>
    </div>`;
    };

    renderTimeOffs() {
        return html`
        <div class="passages-card">
        <table class="table table-sm table-borderless center-table-cells">
        <tr>
            <th>Time Offs:</td>
            <th>${this.passageReport.timeOffsBreakdown.count}</td>
            <th>${this.passageReport.timeOffsBreakdown.durationString}</td>
        <tr>
    </table>
    </div>`;
    }

    renderBOPBreakdown() {
        return html`
        <div class="passages-card">
        <table class="table table-borderless table-sm center-table-cells">
            <thead>
                <tr>
                    <th>BOP BREAKDOWN</th>
                    <th>Count: </th>
                    <th>${this.passageReport.bopBreakdown.totalCount}</th>
                </tr>
                <tr>
                    <th></th>
                    <th>Total Duration: </th>
                    <th>${this.passageReport.bopBreakdown.totalDurationString}</th>
                </tr>
            </thead>
            <tbody>
                ${this.passageReport.bopBreakdown.rangeItems.map((range) =>
            html`
                    <tr>
                        <td>${range.eventCodeName}</td>
                        <td>${range.count}</td>
                        <td>${range.durationString}</td>
                    </tr>`
        )}
            </tbody>
    </table>
    </div>`;
    };

    getColorByBgColor(bgColor) {
        if (!bgColor) { return ''; }
        return (parseInt(bgColor.replace('#', ''), 16) > 0xffffff / 2) ? '#000' : '#fff';
    }

    render() {

        return [
            html`
            <style>
                ${css`${unsafeCSS(passageReportStyles)}`}                     
            </style>

            <div>                

                <div class="row">  
                    <div class="col">
                        ${this.passageReport == null ? '' : this.renderPassageReport()}
                    </div>  

                </div>

                <div id="loading-container" class="row">

                </div>

            </div>`
        ];
    }
}

window.customElements.define('passage-report', PassageReport as any);